/**
 * TanStack Query hook for fetching the report template by exam type.
 *
 * The template defines the empty-report skeleton (organ subsections in
 * canonical order, default text per subsection, the negative-impression
 * sentence). Per-study findings flow separately through useReport.
 *
 * Templates are slow-changing (versioned per release) — `staleTime: Infinity`
 * means we never refetch within a session.
 */
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";

export interface FindingSubsectionTemplate {
  /** Slug, e.g. "lower-chest" — stable id used by the editor schema. */
  kind: string;
  /** Display label, e.g. "LOWER CHEST". */
  label: string;
  /** Negative/default sentence used to seed the chip. */
  default: string;
}

export interface ReportTemplate {
  id: string;
  version: string;
  examType: string;
  displayName?: string;
  negativeImpression: string;
  findings: FindingSubsectionTemplate[];
}

export const templateKeys = {
  all: ["template"] as const,
  examType: (examType: string) => [...templateKeys.all, examType] as const,
  catalog: () => [...templateKeys.all, "catalog"] as const,
};

async function fetchTemplates(): Promise<ReportTemplate[]> {
  const response = await axios.get<ReportTemplate[]>(`${getApiBaseUrl()}/api/templates`, {
    withCredentials: true,
  });
  return response.data;
}

async function fetchTemplate(examType: string): Promise<ReportTemplate> {
  const baseUrl = getApiBaseUrl();
  const encoded = encodeURIComponent(examType);
  const response = await axios.get<ReportTemplate>(`${baseUrl}/api/template/${encoded}`, {
    withCredentials: true,
  });
  return response.data;
}

export interface UseTemplateOptions {
  enabled?: boolean;
}

export function useTemplate(examType: string | null, options: UseTemplateOptions = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: templateKeys.examType(examType ?? ""),
    queryFn: () => fetchTemplate(examType!),
    enabled: enabled && !!examType,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
}

/** Editable base-template catalog. Dynamic multiregion layouts are composed
 * per study and reconstructed through useTemplate(examType). */
export function useTemplates() {
  return useQuery({
    queryKey: templateKeys.catalog(),
    queryFn: fetchTemplates,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
}
